import type { LocaleDictionary } from './types';

/**
 * 简体中文文案。
 *
 * 这不是英文的逐字翻译。中文版按中文的说法来写：记账类应用说「记一笔」而不是
 * 「添加票据」，医疗单据上写的是「就诊人」而不是「这笔是给谁的」。个别地方两种
 * 语言的措辞完全不同，那是有意的。
 *
 * 排版约定：中文与数字、拉丁字母之间留一个空格。
 */
export const zhCN: LocaleDictionary = {
  brand: {
    name: 'Cairn',
    tagline: '一张一张，稳稳攒着。',
    about:
      'Cairn 是登山路上用石头垒起的路标，一次放一块，多年以后依然指得清方向。你的医疗票据也是这样攒起来的。',
  },

  common: {
    save: '保存',
    saving: '保存中…',
    saved: '已保存',
    cancel: '取消',
    done: '完成',
    delete: '删除',
    remove: '移除',
    edit: '编辑',
    add: '添加',
    close: '关闭',
    back: '返回',
    next: '下一步',
    retry: '重试',
    search: '搜索',
    filter: '筛选',
    filters: '筛选',
    clear: '清除',
    clearAll: '全部清除',
    apply: '应用',
    all: '全部',
    none: '无',
    optional: '选填',
    required: '必填',
    loading: '加载中…',
    copy: '复制',
    copied: '已复制',
    confirm: '确认',
    yes: '是',
    no: '否',
    today: '今天',
    of: '/',
    more: '更多',
    showAll: '展开全部',
    showLess: '收起',
    unknown: '未知',
    uncategorized: '未分类',
    everyone: '全家',
    somethingWentWrong: '出了点问题',
    tryAgainOrRefresh: '请重试，或刷新页面。',
  },

  ledger: {
    singular: '账本',
    plural: '账本',
    all: '全部账本',
    allHint: '你有权限的所有账本',
    switch: '切换账本',
    current: '当前查看',
    create: '新建账本',
    createTitle: '新建账本',
    createBody: '一个账本对应一个 HSA 账户：独立的票据、独立的余额、独立的可见范围。',
    name: '账本名称',
    namePlaceholder: '小明的 HSA',
    description: '备注',
    descriptionPlaceholder: '这个账户用来做什么',
    currency: '货币',
    opened: '开户日期',
    openedHelp: '早于该日期的支出不能从这个账户报销。不确定可留空。',
    settings: '账本设置',
    archive: '归档账本',
    archiveConfirmTitle: '归档这个账本？',
    archiveConfirmBody: '所有能看到它的人都将看不到。数据不会删除，管理员可以恢复。',
    receiptCount: '{count} 张票据',
    emptyTitle: '还没有账本',
    emptyBody: '先创建一个账本，然后开始记录票据。',
    noAccessTitle: '还没有账本分享给你',
    noAccessBody: '还没有账本分享给这个账号。请联系邀请你的人。',
  },

  access: {
    title: '可访问的人',
    subtitle: '所有能打开这个账本的人。',
    share: '分享',
    shareTitle: '分享这个账本',
    shareBody: '选择谁能看到，以及他们能做什么。',
    addPeople: '添加成员或群组',
    searchPlaceholder: '搜索成员和群组',
    noMatches: '没有匹配的结果',
    nobodyLeft: '所有人都已经有权限了',
    level: '权限',
    view: '仅查看',
    contribute: '可以记账',
    manage: '完全权限',
    viewHelp: '可以查看票据和统计。',
    contributeHelp: '可以查看全部，并添加、编辑票据。',
    manageHelp: '在此基础上，还可以登记报销、管理分享权限。',
    remove: '移除权限',
    removeConfirmTitle: '移除 {name}？',
    removeConfirmBody: 'Ta 会立即失去这个账本的访问权限。',
    lastManager: '每个账本至少要保留一位拥有完全权限的成员。',
    groupMembers: '{count} 人',
    viaGroup: '来自「{group}」',
    you: '你',
    onlyYou: '只有你',
    sharedWith: '已分享给 {count} 人',
  },

  groups: {
    title: '群组',
    subtitle: '一次给多个人相同的权限。',
    create: '新建群组',
    name: '群组名称',
    namePlaceholder: '记账人员',
    description: '这个群组的用途',
    members: '成员',
    addMember: '添加成员',
    memberCount: '{count} 人',
    empty: '还没有群组',
    emptyHelp: '多人需要相同权限时使用。',
    deleteConfirmTitle: '删除「{name}」？',
    deleteConfirmBody: '群组里的每个人都会失去由它授予的权限。',
  },

  nav: {
    overview: '概览',
    receipts: '票据',
    add: '记一笔',
    insights: '统计',
    settings: '设置',
  },

  auth: {
    signIn: '登录',
    signOut: '退出登录',
    signingIn: '登录中…',
    email: '邮箱',
    password: '密码',
    currentPassword: '当前密码',
    newPassword: '新密码',
    confirmPassword: '再输一次新密码',
    forgotPassword: '忘记密码？',
    welcomeBack: '欢迎回来',
    signInSubtitle: '登录以继续。',
    invalidCredentials: '邮箱或密码不正确。',
    accountDisabled: '该账号已停用，请联系管理员。',
    resetTitle: '重置密码',
    resetSubtitle: '为你的账号设置一个新密码。',
    resetSent: '如果该邮箱已注册，重置链接已发送。',
    resetInvalid: '这个重置链接已经失效或被用过了。',
    resetNoEmail: '未配置邮件服务，无法发送重置链接。管理员可以代为生成。',
    resetDone: '密码已修改，现在可以用新密码登录。',
    passwordChanged: '密码已修改，其他设备都已退出登录。',
    passwordTooShort: '密码至少 10 个字符。',
    passwordMismatch: '两次输入的密码不一致。',
    currentPasswordWrong: '当前密码不正确。',
    strength: {
      label: '强度',
      weak: '偏弱',
      fair: '一般',
      good: '不错',
      strong: '很强',
    },
  },

  setup: {
    title: '开始设置 Cairn',
    subtitle: '创建第一个账号，该账号将成为管理员。',
    workspaceName: '工作区名称',
    workspacePlaceholder: '陈家、某某公司…',
    workspaceHint: '会出现在邀请邮件和导出文件里。',
    firstLedger: '第一个账本',
    firstLedgerHint: '每个 HSA 账户对应一个账本，之后可以继续添加。',
    yourName: '你的名字',
    createAccount: '创建账号',
    creating: '正在设置…',
    languageQuestion: 'Cairn 用哪种语言？',
    almostThere: '就快好了',
    finish: '完成',
  },

  invite: {
    title: '加入{workspace}',
    titleGeneric: '你收到一份邀请',
    subtitle: '设置密码以完成账号创建。',
    yourName: '你的名字',
    accept: '创建账号',
    invalid: '这个邀请链接已经失效或被用过了。',
    invalidHelp: '请索取新的邀请链接。',
    signInInstead: '已经有账号了？去登录',
  },

  dashboard: {
    greetingMorning: '早上好，{name}',
    greetingAfternoon: '下午好，{name}',
    greetingEvening: '晚上好，{name}',
    availableToReimburse: '可报销金额',
    availableExplainer: '已经自己垫付、还没从 HSA 账户报销回来的医疗支出。',
    acrossReceipts: '共 {count} 张票据',
    totalSpent: '累计支出',
    alreadyClaimed: '已报销',
    thisYear: '今年',
    oldestClaimable: '最早一笔待报销',
    oldestClaimableHint: 'HSA 报销没有时间限制。',
    connectPaperless: '连接你的 Paperless',
    connectPaperlessBody:
      'Cairn 把所有单据存在你的 Paperless-ngx 里。指向你的实例之后就能开始上传票据了。',
    connectPaperlessAction: '去连接',
    needsAttention: '需要留意',
    missingDocuments: '有 {count} 张票据还没上传单据',
    missingDocumentsHelp: '没有凭证的报销日后难以证明。',
    needsReview: '有 {count} 张导入的票据待核对',
    strandedWarning: '开户前的支出',
    strandedHelp: '其中 {amount} 发生在 {date} 开户之前，不能从这个 HSA 账户报销。',
    spendingOverTime: '支出趋势',
    cumulativeClaimable: '可报销金额累计',
    byCategory: '花在哪里',
    byPerson: '为谁花的',
    recentReceipts: '最近添加',
    viewAll: '查看全部',
    emptyTitle: '还没有记录',
    emptyBody: '添加一笔票据即可开始。',
    emptyAction: '记一笔',
  },

  receipts: {
    title: '票据',
    searchPlaceholder: '搜索机构、备注或就诊人',
    resultCount: '{count} 张票据',
    totalInView: '当前合计',
    claimableInView: '当前可报销',
    emptyTitle: '还没有票据',
    emptyBody: '添加第一张票据。',
    emptyFilteredTitle: '没有符合条件的票据',
    emptyFilteredBody: '把时间范围放宽，或者去掉一个筛选条件。',
    groupThisMonth: '本月',
    groupLastMonth: '上月',
    sort: {
      label: '排序',
      date_desc: '最新在前',
      date_asc: '最早在前',
      amount_desc: '金额从高到低',
      amount_asc: '金额从低到高',
      added_desc: '按添加时间',
    },
    filter: {
      person: '就诊人',
      category: '类别',
      status: '状态',
      dateRange: '日期',
      amount: '金额',
      payment: '支付方式',
      documents: '单据',
      withDocuments: '已上传单据',
      withoutDocuments: '缺单据',
      from: '从',
      to: '到',
      min: '最低',
      max: '最高',
      anyTime: '不限时间',
      thisYear: '今年',
      lastYear: '去年',
      last12Months: '近 12 个月',
      activeCount: '{count} 个筛选条件',
    },
  },

  status: {
    open: '待报销',
    partial: '部分报销',
    reimbursed: '已报销',
    ineligible: '不可报销',
    openHelp: '可报销。没有申报期限。',
    partialHelp: '共 {total}，已报 {claimed}。',
    reimbursedHelp: '已于 {date} 报销。',
    ineligibleHelp: '已标记为不属于可报销的医疗支出。',
  },

  payment: {
    label: '支付方式',
    out_of_pocket: '自付',
    hsa_card: 'HSA 卡',
    other: '其他方式',
    outOfPocketHelp: '之后可以从 HSA 账户报销回来。',
    hsaCardHelp: '已经直接从 HSA 账户扣款，不能再报一次。',
    otherHelp: '用其他方式支付，不计入可报销金额。',
  },

  eligibility: {
    label: '是否可报销',
    eligible: '可报销',
    unsure: '待确认',
    ineligible: '不可报销',
    unsureHelp: '确认之前不会计入可报销金额。',
  },

  documentKind: {
    label: '单据类型',
    receipt: '收据',
    eob: '保险理赔说明',
    invoice: '账单',
    prescription: '处方',
    statement: '对账单',
    tax_form: '税表（1099-SA / 5498-SA）',
    other: '其他',
  },

  form: {
    newReceipt: '记一笔',
    editReceipt: '编辑票据',
    amount: '金额',
    amountPlaceholder: '0.00',
    person: '就诊人',
    personPlaceholder: '选择就诊人',
    category: '类别',
    categoryPlaceholder: '选择类别',
    provider: '医疗机构',
    providerPlaceholder: '北门口牙科诊所',
    serviceDate: '就诊日期',
    paidDate: '付款日期',
    description: '项目说明',
    descriptionPlaceholder: '洁牙、拍片',
    notes: '备注',
    notesPlaceholder: '记点日后想得起来的事',
    attachDocument: '上传单据',
    takePhoto: '拍照',
    chooseFile: '选择文件',
    addAnother: '保存并继续记',
    saveReceipt: '保存',
    deleteReceipt: '删除票据',
    deleteConfirmTitle: '删除这张票据？',
    deleteConfirmBody: '它会从统计里消失，文件仍然保留在 Paperless 里。',
    deleteAlsoDocument: '同时从 Paperless 删除文件',
    amountRequired: '请填写金额',
    dateRequired: '请选择日期',
    unsavedTitle: '放弃这次编辑？',
    unsavedBody: '改动还没有保存。',
    discard: '放弃',
    keepEditing: '继续编辑',
  },

  detail: {
    title: '票据详情',
    markReimbursed: '标记为已报销',
    markReimbursedPartial: '报销一部分',
    undoReimbursed: '改回待报销',
    reimburseTitle: '登记报销',
    reimburseBody: '记录你从 HSA 账户报回来的金额。',
    reimburseAmount: '报销金额',
    reimburseFull: '全额 {amount}',
    reimburseDate: '报销日期',
    reimburseNote: '凭证号',
    reimburseNotePlaceholder: '转账流水号、支票号…',
    documents: '单据',
    addDocument: '添加单据',
    documentProcessing: '识别中…',
    documentProcessingHelp: 'Paperless 正在扫描，通常不到一分钟。',
    documentFailed: '识别失败',
    documentFailedHelp: '文件还在 Paperless 里，可以重新上传一次。',
    noDocuments: '还没有单据',
    noDocumentsHelp: '把票据传上来，报销时才有据可查。',
    viewerZoom: '缩放',
    viewerFailed: '这个文件加载不出来，可能是连不上 Paperless。',
    viewerUnsupported: '这个文件没法在这里显示，下载后打开吧。',
    download: '下载',
    openInPaperless: '在 Paperless 中打开',
    addedBy: '由 {name} 添加',
    addedOn: '{date} 添加',
    history: '操作记录',
  },

  insights: {
    title: '统计',
    subtitle: '钱都花在哪儿了，还有多少能报回来。',
    range: '时间范围',
    allTime: '全部时间',
    byYear: '按年份',
    byCategoryTitle: '各类别支出',
    byPersonTitle: '各成员支出',
    monthlyTitle: '逐月支出',
    claimedVsOpen: '已报销与待报销',
    claimed: '已报销',
    stillOpen: '待报销',
    spent: '支出',
    noData: '还没有可以统计的数据',
    noDataHelp: '记上几笔，图表自然就出来了。',
    tableView: '看表格',
    chartView: '看图表',
    exportCsv: '导出表格',
    exportArchive: '导出存档',
    exportArchiveHelp: '一份表格加上全部单据，可以直接交给会计。',
    exporting: '正在准备下载…',
  },

  settings: {
    title: '设置',
    account: '账号',
    profile: '个人资料',
    name: '姓名',
    language: '语言',
    appearance: '外观',
    theme: { system: '跟随系统', light: '浅色', dark: '深色' },
    security: '安全',
    changePassword: '修改密码',
    activeSessions: '已登录的设备',
    signOutOthers: '退出其他设备',
    signOutOthersDone: '其他设备已退出登录。',
    thisDevice: '当前设备',
    lastSeen: '最近使用：{date}',
    workspace: '工作区',
    workspaceName: '名称',
    currency: '默认货币',
    currencyHelp: '用于新建账本，每个账本也可以单独设置。',
    ledgers: '账本',
    ledgersHelp: '每个 HSA 账户一个账本，各自决定谁能看到。',
    people: '本账本的成员',
    peopleHelp: '所有需要记账的人，包括没有账号的家人。',
    addPerson: '添加成员',
    removePerson: '移除{name}',
    removePersonTitle: '移除{name}？',
    removePersonBody: '记账时不会再出现这个人。已经记在 Ta 名下的票据不受影响。',
    personRemoved: '已移除',
    personName: '姓名',
    relationship: '关系',
    relationships: {
      self: '本人',
      spouse: '配偶',
      child: '子女',
      parent: '父母',
      dependent: '受抚养人',
      other: '其他',
    },
    personReceipts: '{count} 张票据',
    archivePerson: '归档',
    archivedPerson: '已归档',
    categories: '类别',
    categoriesHelp: '用来归类支出。可以改名，也可以自己加。',
    users: '账号',
    usersHelp: '谁可以登录。能看到什么由每个账本单独决定。',
    invitePerson: '邀请成员',
    connections: '连接',
    paperless: 'Paperless',
    email: '邮件',
    about: '关于',
    version: '版本',
    documentation: '使用文档',
    dangerZone: '谨慎操作',
    publicUrl: '对外访问地址',
    publicUrlHelp: '用来生成邀请链接，一般就是你浏览器地址栏里的地址。',
  },

  users: {
    title: '账号',
    role: '角色',
    admin: '管理员',
    member: '成员',
    adminHelp: '可以创建账号、管理群组、修改实例设置。',
    memberHelp: '普通账号。能看到什么由每个账本单独决定。',
    adminNotice: '管理员不会自动看到任何账本，权限始终需要明确授予。',
    grantAccess: '给予以下账本的权限',
    grantAccessHelp: '选填 —— 也可以之后再分享给 Ta。',
    status: '状态',
    active: '正常',
    invited: '待接受邀请',
    disabled: '已停用',
    neverSignedIn: '从未登录',
    lastSignIn: '最近登录：{date}',
    inviteTitle: '邀请成员',
    inviteBody: '密码由对方自己设置，你这边不用做任何事。',
    inviteEmail: '对方邮箱',
    inviteName: '对方姓名',
    sendEmail: '给对方发邮件',
    sendEmailUnavailable: '没有配置邮件服务，这里会给你一个链接，自己发给对方。',
    inviteSent: '邀请已发送至 {email}。',
    inviteLinkReady: '邀请链接已生成，发给对方即可。',
    inviteLinkHelp: '拿到链接的人都能加入，请直接发给本人。',
    pendingInvites: '待接受的邀请',
    expires: '{date} 失效',
    resend: '换新链接',
    revoke: '取消邀请',
    resetPassword: '重置密码',
    resetLinkReady: '把这个链接发给对方，让 Ta 设置新密码。',
    disableUser: '停用',
    enableUser: '重新启用',
    removeUser: '移除',
    removeConfirmTitle: '移除 {name}？',
    removeConfirmBody: 'Ta 会立即失去访问权限，之前添加的票据仍然保留。',
    lastAdmin: '至少要留一位管理员，先把别人升为管理员吧。',
    emailTaken: '这个邮箱已经有账号了。',
  },

  paperless: {
    title: 'Paperless',
    subtitle:
      'Cairn 把所有文件存在你自己的 Paperless-ngx 里，并读取 Paperless 识别出的信息。它自己不做 OCR，也不负责安装 Paperless。',
    notConnected: '尚未连接',
    notConnectedHelp: '指向你的 Paperless 之后就能上传单据了。',
    connected: '已连接',
    connectedTo: '已连接到 {url}',
    documentCount: '归档中有 {count} 份文件',
    serverUrl: 'Paperless 地址',
    serverUrlPlaceholder: 'http://paperless:8000',
    username: 'Paperless 用户名',
    passwordLabel: 'Paperless 密码',
    passwordHelp: '仅用于换取一次 API 令牌，不会保存。',
    useToken: '改用 API 令牌',
    usePassword: '改用用户名和密码',
    apiToken: 'API 令牌',
    connect: '连接',
    connecting: '连接中…',
    test: '测试连接',
    testOk: '连接正常，Paperless 有回应。',
    disconnect: '断开连接',
    unreachable: '连不上这个地址。检查一下网址，以及 Paperless 是否在运行。',
    unauthorized: 'Paperless 拒绝了这组凭据。',
    importTitle: '导入已有文件',
    importBody: 'Paperless 里已经有医疗单据？可以按 Paperless 已记录的信息导入成草稿，慢慢核对。',
    importTag: '按标签导入',
    importPreview: '有 {count} 份文件可以导入',
    importAction: '导入',
    importDone: '已导入 {count} 张票据。',
    importNothing: '没有新的可以导入。',
  },

  sheets: {
    title: 'Google 表格备份',
    subtitle:
      '在你自己的表格里保留每一张票据的副本。即使这台服务器、硬盘和备份全部丢失，记录依然在：金额、日期、就诊人，以及指向原始文件的链接。',
    off: '已关闭',
    on: '已开启',
    connected: '正在备份',
    notConnected: '尚未设置',
    setUp: '设置',
    enable: '备份到 Google 表格',
    enableHelp: '可选。开启之前，任何数据都不会离开这台服务器。',
    spreadsheet: '表格',
    spreadsheetHelp: '粘贴表格的网址，或只填它的 ID。',
    spreadsheetPlaceholder: 'https://docs.google.com/spreadsheets/d/…',
    serviceAccount: '服务账号密钥',
    serviceAccountHelp: 'Google 服务账号的 JSON 密钥文件，把内容粘贴到这里。',
    serviceAccountSaved: '已保存 {email} 的密钥',
    shareReminder: '把表格共享给 {email} 并授予编辑者权限，否则 Google 会拒绝写入。',
    frequency: '备份频率',
    frequencyEntry: '每次改动后',
    frequencyHourly: '每小时',
    frequencyDaily: '每天',
    frequencyWeekly: '每周',
    frequencyManual: '仅手动',
    tabsNote: '每个账本对应一个工作表，每次整表重写，修改和删除都会同步过去。',
    test: '测试连接',
    testOk: '已连接到 {title}。',
    syncNow: '立即备份',
    syncing: '备份中…',
    syncOk: '已备份 {ledgers} 个账本共 {rows} 张票据。',
    syncNothing: '暂时没有可备份的内容。',
    lastSynced: '上次备份于 {when}',
    neverSynced: '尚未备份',
    lastError: '上次备份失败：{message}',
    setupTitle: '设置备份',
    setupSteps:
      '在 Google Cloud 控制台中：新建项目、启用 Google Sheets API、创建服务账号并下载 JSON 密钥。然后新建一个表格，把它共享给该服务账号并设为编辑者。',
  },

  emailSettings: {
    title: '邮件',
    subtitle: '选填。不配置也能用，Cairn 会给你链接自己转发。',
    host: 'SMTP 服务器',
    port: '端口',
    secure: '使用 TLS',
    username: '用户名',
    password: '密码',
    from: '发件人',
    test: '发送测试邮件',
    testOk: '邮件服务器有回应。',
    testFailed: '连不上这个邮件服务器。',
  },

  errors: {
    offline: '看起来网络断开了。',
    generic: '出了点问题。',
    notFound: '没找到这个页面。',
    notFoundTitle: '找不到票据',
    couldNotLoad: '无法加载',
    couldNotLoadBody: '网络中断，或服务器繁忙。数据没有丢失，请重试。',
    notFoundBody: '可能已经被删除，也可能链接不对。',
    goHome: '回到概览',
    sessionExpired: '登录已过期，请重新登录。',
    fileTooLarge: '文件太大了。',
    paperlessNotConfigured: '请先连接 Paperless 才能上传单据。',
    validation: '请检查标出来的几项。',
  },

  a11y: {
    menu: '菜单',
    closeSheet: '关闭面板',
    chartDescription: '{title}图表。同样的数据在图表下方的表格里也有。',
    documentPreview: '{filename} 的预览',
    memberColor: '{name} 的颜色标记',
  },
};
